<script>
    import {
        CardLayout,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import { PdfTemplateTypes } from "../../../Utils/utilEnums.svelte";

    import {
        getAmountInCurrency,
        getFormatDate,
        getFormatDateAndTime,
        getText,
        goToDownloadPage,
        goToURL,
        isObjectEmpty,
    } from "../../../Utils/utilFunctions.svelte";

    export let groupInvoices = [];

    const columns = [
        {
            key: "id",
            title: "Invoice ID",
            headerClass: "text-left px-3 rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "group",
            title: "Group",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getGroupName(v.billingDetails),
        },
        {
            key: "month",
            title: "Month",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                _getFormatDate(v.createdDate, false, true, true),
        },
        {
            key: "createdDate",
            title: "Create Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.createdDate, false, true),
        },
        {
            key: "dueDate",
            title: "Due Date",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.dueDate, false, true),
        },
        {
            key: "subTotal",
            title: "Subtotal",
            headerClass: "text-right px-3 cursor-default",
            class: "text-right px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-right px-3 cursor-default",
            class: "text-right px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee / 10),
        },
        {
            key: "totalFee",
            title: "Total",
            headerClass: "text-right px-3 cursor-default",
            class: "text-right px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(v.totalFee * (v.addGST ? 1.1 : 1)),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-right px-3 cursor-default",
            class: "text-right px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-center px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadGroupInvoice(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateExternalGroupInvoice(rowData);
        } else if (rowData.id) goToURL(`/GroupInvoices/view/${rowData.id}`);
    };

    const downloadGroupInvoice = (id) => {
        goToDownloadPage(PdfTemplateTypes.ExternalGroupInvoice, id, false);
    };

    const regenerateExternalGroupInvoice = async (data) => {
        const { id, createdDate, groupId } = data;

        const regenerateObj = {
            groupId,
            id,
            createdDate,
        };

        await apiMethods.putMethod
            .regenerateExternalGroupInvoice(regenerateObj)
            .then((res) => {
                if (res.id)
                    goToDownloadPage(
                        PdfTemplateTypes.ExternalGroupInvoice,
                        res.id,
                        true,
                    );
                else
                    showError(
                        "Failed to re-generate this invoice, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );
    };

    // HELPER FUNCTIONS
    const _getGroupName = (billingDetails) => {
        if (isObjectEmpty(billingDetails)) return "";

        return getText(billingDetails.name);
    };

    const _getFormatDate = (date, isExpired, isDateOnly, hideDate = false) => {
        if (isExpired) return "Expired";

        if (isDateOnly)
            return hideDate
                ? getFormatDate(date).substring(3)
                : getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getStatus = (status) => {
        let statusText, statusColor;

        switch (status) {
            case 1:
                statusText = "Active";
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusText = "Cancelled";
                statusColor = "#E05757";
                break;
            default:
                statusText = "";
                statusColor = "";
        }

        return `
                <div class="flex justify-end items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-center gap-x-2">
                    <div class="reGenerateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Re-generate Invoice">
                        <i class="reGenerateIcon fas fa-arrows-rotate"></i>
                    </div>
                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Invoice PDF">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>
                </div>`;
    };
</script>

<CardLayout title="Group Invoices">
    {#if !groupInvoices.length}
        <div class="flex px-2 py-4 w-full">
            <p>No invoices currently under this group.</p>
        </div>
    {:else}
        <div class="w-full overflow-x-auto">
            <SvelteTable
                {columns}
                rows={groupInvoices}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
            />
        </div>
    {/if}
</CardLayout>
